/**
 * @desc       	Alerts Config Helper
 * @version     1.3 (Farm select clears alerts even when the stored list is missing) - 4 November 2026
 *              1.2 (delete thunk fix) - 13 September 2024
 * */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import Axios from 'axios'
import { FARMDECK_API } from 'app-config/index.js'
import { accountId } from 'helpers/account.js'
import * as actionTypes from 'redux/actions/action-types'

/** Resolved per call, never at module load: `accountId` reads the page path for a master user, so a constant is frozen to the page open when this module loaded. */
const baseUrl = () => `${FARMDECK_API.replace(':id', accountId)}`

export const fetchAlerts = createAsyncThunk(
    'alerts/fetchAlerts',
    async ({ id, force = false, cache = false, tenant } = {}, { getState, rejectWithValue }) => {
        const { lastFetch, data } = getState().alerts
        const timeSinceLastFetch = Date.now() - lastFetch
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        if (lastFetch === 0 || force || !cache || timeSinceLastFetch >= 5 * 60 * 1000) {
            try {
                const endpoint = id ? `assets/${id}/alertconfigs` : `alertconfigs`
                const response = await Axios.get(`${apiURL}${endpoint}`)
                return response.data
            } catch (error) {
                return rejectWithValue(error.toString())
            }
        }

        // Return cached items if not forced to fetch, cache is true, and within cache validity
        return data.items
    },
    {
        condition: ({ force, cache } = {}, { getState }) => {
            if (force) return true // Always proceed if force is true
            const { alerts } = getState()
            if (alerts?.loading) return false
            // Proceed if cache is false or it's the first fetch or data is stale
            return cache && alerts.lastFetch !== 0 && shouldFetch(alerts)
        },
    },
)

export const createAlertWithoutId = createAsyncThunk('alerts/createAlertWithoutId', async ({ data, tenant }, { rejectWithValue }) => {
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const response = await Axios.post(`${apiURL}alertconfigs`, data)
        return response.data
    } catch (error) {
        return rejectWithValue(error.toString())
    }
})
// TODO: cut this away at some point, as it no longer used
export const createAlert = createAsyncThunk('alerts/createAlert', async ({ assetId, data, tenant }, { rejectWithValue }) => {
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const response = await Axios.post(`${apiURL}assets/${assetId}/alertconfigs`, data)
        return response.data
    } catch (error) {
        return rejectWithValue(error.toString())
    }
})

export const updateAlert = createAsyncThunk('alerts/updateAlert', async ({ id, data, tenant }, { getState, rejectWithValue }) => {
    const originalItems = getState().alerts.data.items
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        const response = await Axios.put(`${apiURL}alertconfigs/${id}`, data)
        return response.data
    } catch (error) {
        rejectWithValue(error.toString())
        return { id, originalItems, error: { status: error?.response?.status, data: error?.response?.data } } // Return original items for potential rollback
    }
})

export const deleteAlert = createAsyncThunk('alerts/deleteAlert', async ({ id, tenant }, { getState, rejectWithValue }) => {
    const originalItems = getState().alerts.data.items
    try {
        const apiURL = tenant ? FARMDECK_API.replace(':id', tenant) : baseUrl()
        await Axios.delete(`${apiURL}alertconfigs/${id}`)
        return id // Return the id of the deleted item
    } catch (error) {
        rejectWithValue(error.toString())
        return { id, originalItems } // Return original items for potential rollback
    }
})

// Helper function to determine if the cache should be used
const shouldFetch = state => !state.loading && Date.now() - state.lastFetch > 5 * 60 * 1000

const initialState = {
    data: {
        items: [],
    },
    loading: false,
    error: null,
    lastFetch: 0,
}

const alertsSlice = createSlice({
    name: 'alerts',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchAlerts.pending, state => {
                state.loading = true
            })
            .addCase(fetchAlerts.fulfilled, (state, action) => {
                state.loading = false
                state.data = {
                    items: action?.payload || [],
                }
                state.lastFetch = Date.now()
                state.error = null
            })
            .addCase(fetchAlerts.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            .addCase(createAlert.fulfilled, (state, action) => {
                state.data.items.push(action.payload)
            })
            .addCase(createAlertWithoutId.fulfilled, (state, action) => {
                state.data.items.push(action.payload)
            })
            .addCase(updateAlert.fulfilled, (state, action) => {
                const index = state.data.items.findIndex(item => item.id === action.meta.arg.id)
                if (index !== -1) {
                    state.data.items[index] = { ...state.data.items[index], ...action.payload }
                }
            })
            .addCase(deleteAlert.fulfilled, (state, action) => {
                // intermittant location of the id :-(
                // happens when the request is rejected sometimes
                const id = action?.payload?.id || action?.payload || 0
                state.data.items.splice(0, state.data.items.length, ...state.data.items.filter(item => item.id !== id))
            })
            // Handle optimistic update rollback in rejected cases for update and delete
            .addCase(updateAlert.rejected, (state, action) => {
                if (action.payload.originalItems) {
                    state.data.items.splice(0, state.data.items.length, ...action.payload.originalItems)
                }
                state.error = action.payload
            })
            .addCase(deleteAlert.rejected, (state, action) => {
                console.log('reject', state, action)
                if (action.payload.originalItems) {
                    state.data.items.splice(0, state.data.items.length, ...action.payload.originalItems)
                }
                state.error = action.payload
            })
            .addCase(actionTypes.AWS_UTT_SET_CURRENT, state => {
                // A rehydrated slice can have `data` and no `items`. Setting `.length` on that throws and aborts farm select.
                state.data = { items: [] }
            })
            .addCase(actionTypes.AWS_USER_REQUEST_CLEAR, state => {
                state.data = { items: [] }
                state.lastFetch = initialState.lastFetch
                state.error = initialState.error
                state.loading = initialState.loading
            })
    },
})

export default alertsSlice.reducer
